import { useStoreState } from "ezStore";
import { intl } from "LocaleWrapper";
import moment from "moment";

import { FilterGroups } from "@cofano/cck";

import RelationSelect from "view/relations/components/selects/RelationSelect";
import ProductSelect from "view/products/components/select/ProductSelect";
import ShipSelect from "view/ships/components/selects/ShipSelect";
import VoyageSelect from "components/selects/VoyageSelect";
import LocationSelect from "view/locations/components/selects/LocationSelect";
import CountrySelect, { countries } from "components/selects/CountrySelect";
import StatusSelect from "view/statuses/components/selects/StatusSelect";

import { Fields, hasPluginPermission, hasOptionalFieldPermission, DataFormat, isAdmin, isCompanyUser, isMainUserLimited, isCustomer, isOperator, isFinance } from "imports/Utils";
import { FormattedDateWindow } from "utils/DateUtils";
import SalesCharterStatusSelect from "view/charters/components/selects/SalesCharterStatusSelect";
import CharterSubtypeSelect from "view/charters/components/selects/CharterSubtypeSelect";
import { CharterSubtype, RelationType } from "__generated__/enums";

export const CargoFilterType = {
	GENERAL: "GENERAL",
	UNPLANNED: "UNPLANNED",
};

const currentBreakPoints = {
	xs: 12,
	sm: 12,
	md: 12,
	lg: 6,
	xl: 3,
};

export const convertFilterToJSON = (filter) => {

	const object = {};

	Object.entries(filter).forEach(([ key, value ]) => {
		if (!isNaN(Number(key))) {
			key = value.property;
			value = value.value;
		}
		if (key === "isPlanned") {
			object["isUnplanned"] = !value;
		} else if (key === "loadingWindow" || key === "dischargingWindow") {
			object[key] = {
				from: moment(value[0]).unix() * 1000,
				to:  moment(value[1]).unix() * 1000,
			};
		} else if (key === "created") {
			object[key] = {
				from: value[0],
				to:  value[1] || undefined,
			};
		} else {
			object[key] = Array.isArray(value) ? value.map(({ id }) => id) : value.id || value;
		}
	});
	delete object.type; //somehow a 'type' filter gets included here and breaks the request
	return object;
};

const CargoFilter = ({ controlledValues, filterRef, id, onSearch, onChange, settingsProvider, type = CargoFilterType.GENERAL, newBreakPoints }) => {

	const session = useStoreState(state => state.session);
	const { statuses } = session;

	const breakPoints = newBreakPoints || currentBreakPoints;

	return (
		<FilterGroups
			id={id}
			controlledValues={controlledValues}
			onSearch={onSearch}
			onChange={onChange}
			filterRef={filterRef}
			settingsProvider={settingsProvider}
			fields={[ {
				id: "id",
				title: "ID",
				filterType: "NUMBER",
			}, {
				id: "charterNumber",
				title: intl.formatMessage({ id: "CHARTER_ORDER_NUMBER" }),
				filterType: "TEXT",
			}, {
				id: "charterStatus",
				title: intl.formatMessage({ id: "CHARTER_ORDER_STATUS" }),
				render: (value, onFieldChange) => (
					<SalesCharterStatusSelect
						value={value}
						multiple
						onChange={onFieldChange}
					/>
				),
			}, {
				id: "charterSubtype",
				title: intl.formatMessage({ id: "CHARTER_TYPE" }),
				render: (value, onFieldChange) => (
					<CharterSubtypeSelect
						value={value}
						onChange={onFieldChange}
						multiple
					/>
				),
			}, {
				id: "relations",
				title: intl.formatMessage({ id: "ORDER_CUSTOMER" }),
				render: (value, onFieldChange) => (
					<RelationSelect
						value={value}
						onChange={(options) => onFieldChange(options)}
						types={[ "CUSTOMER", "OPERATOR" ]}
						multiple />
				),
				renderFilterValue: (value) => value?.map(item => item.name).join(", "),
				disabled: !(hasOptionalFieldPermission(session, Fields.ORDER, "relation")),
			}, {
				id: "brokers",
				title: intl.formatMessage({ id: "BROKER" }),
				render: (value, onFieldChange) => (
					<RelationSelect
						types={[ RelationType.Broker ]}
						value={value}
						onChange={onFieldChange}
						multiple
					/>
				),
			}, {
				id: "products",
				title: intl.formatMessage({ id: "PRODUCT" }),
				render: (value, onFieldChange) => (
					<ProductSelect
						value={value}
						onChange={(options) => onFieldChange(options)}
						multiple />
				),
				renderFilterValue: (value) => value?.map(item => item.name).join(", "),
				disabled: !hasPluginPermission(session, "products"),
			}, {
				id: "productDetails",
				title: intl.formatMessage({ id: "PRODUCT_DETAILS" }),
				filterType: "TEXT",
			}, {
				id: "ships",
				title: intl.formatMessage({ id: "VOYAGE_SHIPS" }),
				render: (value, onFieldChange) => (
					<ShipSelect
						value={value}
						onChange={(options) => onFieldChange(options)}
						multiple />
				),
				renderFilterValue: (value) => value?.map(item => item.name).join(", "),
				disabled: !hasPluginPermission(session, "voyages") || type === CargoFilterType.UNPLANNED
					|| !(isAdmin(session) || isCompanyUser(session) || isMainUserLimited(session)
						|| isCustomer(session) || isOperator(session) || isFinance(session)),
			}, {
				id: "voyages",
				title: intl.formatMessage({ id: "VOYAGE_NUMBER" }),
				render: (value, onFieldChange) => (
					<VoyageSelect
						multiple
						value={value}
						includeExpired={true}
						onChange={(options) => onFieldChange(options)} />
				),
				disabled: type === CargoFilterType.UNPLANNED,
			}, {
				id: "currentStatuses",
				title: intl.formatMessage({ id: "STATUS" }),
				render: (value, onFieldChange) => (
					<StatusSelect
						value={value}
						onChange={(options) => onFieldChange(options)}
						statuses={statuses?.CARGO ?? []}
						multiple />
				),
				renderFilterValue: (value) => value?.map(item => item.description).join(", "),
				disabled: !hasPluginPermission(session, "cargoStatuses"),
			}, {
				id: "hasDangerousGoods",
				title: intl.formatMessage({ id: "CARGO_DANGEROUS_GOOD" }),
				filterType: "BOOLEAN",
				renderFilterValue: (value) => DataFormat.booleanAsText(value),
			}, {
				id: "loadingCountries",
				title: intl.formatMessage({ id: "ORDER_LOADING_COUNTRY" }),
				render: (value, onChange) => <CountrySelect value={value} onChange={onChange} />,
				renderFilterValue: (value) => countries.find(c => c.code === value)?.name || value,
			}, {
				id: "dischargingCountries",
				title: intl.formatMessage({ id: "ORDER_DISCHARGING_COUNTRY" }),
				render: (value, onChange) => <CountrySelect value={value} onChange={onChange} />,
				renderFilterValue: (value) => countries.find(c => c.code === value)?.name || value,
			}, {
				id: "loadingLocations",
				title: intl.formatMessage({ id: "ORDER_LOADING_PLACE" }),
				render: (value, onFieldChange) => (
					<LocationSelect
						value={value}
						onChange={(options) => onFieldChange(options)}
						multiple />
				),
				renderFilterValue: (value) => value?.map(item => item.description).join(", "),
			}, {
				id: "dischargingLocations",
				title: intl.formatMessage({ id: "ORDER_DISCHARGING_PLACE" }),
				render: (value, onFieldChange) => (
					<LocationSelect
						value={value}
						onChange={(options) => onFieldChange(options)}
						multiple />
				),
				renderFilterValue: (value) => value?.map(item => item.description).join(", "),
			}, {
				id: "loadingWindow",
				title: intl.formatMessage({ id: "ORDER_LOADING_DATE" }),
				filterType: "DATE_RANGE",
				renderFilterValue: (value) => <FormattedDateWindow from={value[0]} to={value[1]} withoutYear />,
				disabled: !hasOptionalFieldPermission(session, Fields.ORDER, "loadingDate"),
			}, {
				id: "dischargingWindow",
				title: intl.formatMessage({ id: "ORDER_DISCHARGING_DATE" }),
				filterType: "DATE_RANGE",
				renderFilterValue: (value) => <FormattedDateWindow from={value[0]} to={value[1]} withoutYear />,
				disabled: !hasOptionalFieldPermission(session, Fields.ORDER, "dischargingDate"),
			}, {
				id: "searchLabel",
				title: intl.formatMessage({ id: "ORDER_SEARCH_LABEL" }),
				filterType: "TEXT",
				disabled: !hasOptionalFieldPermission(session, Fields.ORDER, "searchLabel"),
			}, {
				id: "loadingReference",
				title: intl.formatMessage({ id: "LOADING_REFERENCE" }),
				filterType: "TEXT",
				disabled: !hasOptionalFieldPermission(session, Fields.ORDER, "loadingReference"),
			}, {
				id: "dischargingReference",
				title: intl.formatMessage({ id: "DISCHARGING_REFERENCE" }),
				filterType: "TEXT",
				disabled: !hasOptionalFieldPermission(session, Fields.ORDER, "dischargingReference"),
			}, {
				id: "created",
				title: intl.formatMessage({ id: "ORDER_CREATED" }),
				filterType: "DATE_RANGE",
				renderFilterValue: (value) => <FormattedDateWindow from={value[0]} to={value[1]} withoutYear />,
			}, {
				id: "isPlanned",
				title: intl.formatMessage({ id: "CARGO_PLANNED" }),
				filterType: "BOOLEAN",
				renderFilterValue: (value) => DataFormat.booleanAsText(value),
			} ].map((field) => ({
				breakPoints,
				...field,
			}))}
		/>
	);
};

export default CargoFilter;
